import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useI18n } from '@oneaudi/i18n-context';
import { Headline } from '@oneaudi/unified-web-components';
import EquipmentsHighlights from './EquipmentsHighlights';
import {
  CompleteVehicleEntry,
  Features,
  useDesktopOrMobileView,
  useServicesContext,
} from '@oneaudi/vtp-shared';
import { EquipmentsPagination } from './EquipmentsPagination';
import {
  SYS_BREAKPOINT_LG,
  SYS_BREAKPOINT_MD,
  SYS_BREAKPOINT_XL,
} from '@oneaudi/unified-web-common';
import { TrackingServiceV2 } from '@oneaudi/audi-tracking-service';
import { LocaleServiceV1 } from '@volkswagen-onehub/locale-service';
import { registerImpressionTracking, trackFeatureAppReady } from './tracking';
import { splitEquipments } from './utils';
import { HeadlineWrapper } from './styles';
import { StandardEquipmentTable } from './StandardEquipmentTable';

export interface Props {
  vehicle: CompleteVehicleEntry;
}

export const Equipments: React.FC<Props> = ({ vehicle }) => {
  const specialEquipmentHeadline = useI18n({
    id: 'nemo.ui.sc.details.optionalequipment.headline',
    defaultMessage: '',
  });
  const standardEquipmentHeadline = useI18n({
    id: 'nemo.ui.sc.details.standardequipment.headline',
    defaultMessage: '',
  });

  const { features, additionalEquipments } = vehicle.detail;

  if (!features || features.length < 1) {
    return null;
  }

  const specialEquipments = features.filter((feature) => feature.kind === 'extra' && feature.prno);
  const standardEquipments = features.filter((feature) => feature.kind === 'serie' && feature.prno);

  const equipments = features.filter(
    (feature) =>
      feature.kind === (standardEquipments.length > 0 ? 'serie' : 'extra') && feature.prno
  );

  if (equipments.length < 1) {
    return null;
  }

  const isAtLeastMBreakpoint = useDesktopOrMobileView(SYS_BREAKPOINT_MD) === 'desktop';
  const isAtLeastLBreakpoint = useDesktopOrMobileView(SYS_BREAKPOINT_LG) === 'desktop';
  const isAtLeastXLBreakpoint = useDesktopOrMobileView(SYS_BREAKPOINT_XL) === 'desktop';

  const itemsPerView = useMemo(() => {
    if (!isAtLeastMBreakpoint) {
      return 1;
    }
    if (!isAtLeastLBreakpoint) {
      return 3;
    }
    if (!isAtLeastXLBreakpoint) {
      return 4;
    }
    return 5;
  }, [isAtLeastMBreakpoint, isAtLeastLBreakpoint, isAtLeastXLBreakpoint]);

  const splittedSpclEquipments = splitEquipments(specialEquipments);
  const spclEqpmntsAllKinds = [
    ...splittedSpclEquipments.withImages,
    ...splittedSpclEquipments.rest,
  ];
  const spclEqpmntsAllKindsCount = spclEqpmntsAllKinds?.length;
  const numberOfPagesForSpclEqpmntsImages = useMemo(() => {
    return Math.ceil(spclEqpmntsAllKindsCount / itemsPerView);
  }, [spclEqpmntsAllKindsCount, itemsPerView]);

  const splittedStdEquipments = splitEquipments(standardEquipments);
  const stdEqpmntsWithImages = splittedStdEquipments?.withImages;
  const stdEqpmntsWithImagesCount = stdEqpmntsWithImages?.length;
  const numberOfPagesForStdEqpmntsImages = useMemo(() => {
    return Math.ceil(stdEqpmntsWithImagesCount / itemsPerView);
  }, [stdEqpmntsWithImagesCount, itemsPerView]);

  const [currentPageSpcl, setCurrentPageSpcl] = useState(0);
  const [currentPageStd, setCurrentPageStd] = useState(0);

  const getPaginatedImages = (images: Features[], currentPage: number) => {
    const startIndex = currentPage * itemsPerView;
    const endIndex = startIndex + itemsPerView;
    return images.slice(startIndex, endIndex);
  };

  const getCurrentSpclEquipmentsHighlights = useMemo(() => {
    return getPaginatedImages(spclEqpmntsAllKinds, currentPageSpcl);
  }, [spclEqpmntsAllKinds, currentPageSpcl, itemsPerView]);

  const getCurrentStdEquipmentsHighlights = useMemo(() => {
    return getPaginatedImages(stdEqpmntsWithImages, currentPageStd);
  }, [stdEqpmntsWithImages, currentPageStd, itemsPerView]);

  const handlePageChange = (
    eventName: 'prev' | 'next',
    setPage: React.Dispatch<React.SetStateAction<number>>,
    maxPage: number
  ) => {
    setPage((prev) => {
      if (eventName === 'prev') return Math.max(prev - 1, 0);
      if (eventName === 'next') return Math.min(prev + 1, maxPage - 1);
      return prev;
    });
  };

  const onSpclEqpmntsPageChange = (eventName: 'prev' | 'next') => {
    handlePageChange(eventName, setCurrentPageSpcl, numberOfPagesForSpclEqpmntsImages);
  };

  const onStdEqpmntsPageChange = (eventName: 'prev' | 'next') => {
    handlePageChange(eventName, setCurrentPageStd, numberOfPagesForStdEqpmntsImages);
  };
  useEffect(() => {
    setCurrentPageSpcl(0);
    setCurrentPageStd(0);
  }, [itemsPerView]);

  const equipmentFeatures = [
    {
      kind: 'extra',
      headline: specialEquipmentHeadline,
      highlights: getCurrentSpclEquipmentsHighlights,
      totalPages: numberOfPagesForSpclEqpmntsImages,
      current: currentPageSpcl,
      onPageChange: onSpclEqpmntsPageChange,
    },
    {
      kind: 'serie',
      headline: standardEquipmentHeadline,
      highlights: getCurrentStdEquipmentsHighlights,
      totalPages: numberOfPagesForStdEqpmntsImages,
      current: currentPageStd,
      onPageChange: onStdEqpmntsPageChange,
    },
  ];

  const equipmentFeaturesWithData = equipmentFeatures.filter((item) => item.highlights.length > 0);
  if (!equipmentFeaturesWithData.length) {
    return null;
  }
  const localeService = useServicesContext().getAdditionalService(
    'gfa:locale-service'
  ) as LocaleServiceV1;

  const trackingService = useServicesContext().getAdditionalService(
    'audi-tracking-service'
  ) as TrackingServiceV2;
  const equipmentRef = useRef(null);
  useEffect(() => {
    trackFeatureAppReady(trackingService);
    registerImpressionTracking(trackingService, equipmentRef);
  }, []);
  return (
    <>
      {equipmentFeaturesWithData.map((equipmentFeature) => (
        <div ref={equipmentRef} key={equipmentFeature.headline} data-testid="equipments">
          <HeadlineWrapper data-testid="headline">
            {!!equipmentFeature && (
              <Headline
                tag="h3"
                variant={{ type: 'headline', order: '4', weight: 'normal' }}
                data-testid={`${equipmentFeature.kind}_headline`}
              >
                {equipmentFeature.headline}
              </Headline>
            )}
          </HeadlineWrapper>
          <EquipmentsHighlights
            highlights={equipmentFeature.highlights}
            max={itemsPerView}
            vehicleRaw={vehicle}
          />
          <EquipmentsPagination
            totalPages={equipmentFeature.totalPages}
            current={equipmentFeature.current}
            onPageChange={equipmentFeature.onPageChange}
          />
          {equipmentFeature.kind === 'serie' && (
            <StandardEquipmentTable
              features={splittedStdEquipments.rest}
              additionalFeatures={additionalEquipments}
              fod={vehicle.basic.fodInfo}
              countryCode={localeService.countryCode}
              vehicleRaw={vehicle}
            />
          )}
        </div>
      ))}
    </>
  );
};
